import { useState } from "react";
import { Icon } from "@iconify/react";
import type { ConditionStatus } from "@/features/patient/records/api/dataPipelineApi";
import type { ConditionStatusInfo } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import { cn } from "@/shared/lib/utils";
import { toastError, toastSuccess } from "@/shared/lib/toast";

/**
 * Active / Inactive / Resolved for a diagnosis or health concern (decided
 * 2026-10-21). A provider's record: the hospital's record is never changed —
 * the patient's choice is kept beside it and both are named. The patient's own
 * concern: its status is simply changed. The patient themselves only.
 */
export const CONDITION_STATUS_LABEL: Record<ConditionStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  resolved: "Resolved",
};

const STATUSES: ConditionStatus[] = ["active", "inactive", "resolved"];

function formatDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function ConditionStatusControl({
  info, onChange, canEdit, className,
}: {
  info: ConditionStatusInfo;
  /** Saves the new status; rejects with a message on failure. Absent: read-only. */
  onChange?: (status: ConditionStatus) => Promise<void>;
  /** False while viewing someone else's profile: only the patient can change it. */
  canEdit: boolean;
  className?: string;
}) {
  const [saving, setSaving] = useState<ConditionStatus | null>(null);
  const editable = canEdit && !!onChange;

  const pick = async (next: ConditionStatus) => {
    if (!onChange || saving || next === info.status) return;
    setSaving(next);
    try {
      await onChange(next);
      toastSuccess(`Marked as ${CONDITION_STATUS_LABEL[next].toLowerCase()}`);
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Couldn't update the status. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  const hospital = CONDITION_STATUS_LABEL[info.hospitalStatus];
  // The patient's own concern needs no "who said so": everything on My Concerns is theirs.
  let caption: React.ReactNode = null;
  if (info.source === "patient") {
    caption = null;
  } else if (info.mark && info.hospitalNewer) {
    caption = (
      <span className="text-[#946200]" data-testid="status-hospital-newer">
        Your hospital's newer record says {hospital}. You had marked it {CONDITION_STATUS_LABEL[info.mark.status].toLowerCase()} on {formatDay(info.mark.setAt)}.
      </span>
    );
  } else if (info.mark) {
    caption = `Marked by you on ${formatDay(info.mark.setAt)} · Hospital record: ${hospital}`;
  } else {
    caption = "From your hospital record";
  }

  return (
    <div className={cn("grid gap-1.5", className)} data-testid="condition-status-control">
      <p className="!mb-0 text-[13px] font-bold text-[#180047]">Status</p>
      {editable ? (
        <div role="radiogroup" aria-label="Status" className="inline-flex w-fit rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-0.5">
          {STATUSES.map((s) => {
            const selected = info.status === s;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={saving !== null}
                onClick={() => void pick(s)}
                className={cn(
                  "inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors disabled:cursor-wait",
                  // ! so a global button colour can't turn the selected label dark on navy.
                  selected ? "!bg-[#180047] !text-white shadow-sm hover:!text-white" : "!text-[#4B5563] hover:!bg-white hover:!text-[#180047]",
                )}
              >
                {saving === s && <Icon icon="lucide:loader-2" className="h-3.5 w-3.5 animate-spin" />}
                {CONDITION_STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="!mb-0 text-sm font-semibold text-[#180047]">{CONDITION_STATUS_LABEL[info.status]}</p>
      )}
      {caption && <p className="!mb-0 text-[11px] leading-4 text-[#6B7280]" data-testid="condition-status-caption">{caption}</p>}
      {!canEdit && onChange && (
        <p className="!mb-0 text-[11px] text-[#9CA3AF]">Only the patient can change the status.</p>
      )}
    </div>
  );
}
